import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Normaliza cantidades y recalcula los precios de paquete.
 * Un item con `paquete_de` solo conserva su descuento mientras el
 * producto principal siga en el carrito.
 */
function normalizar(lista) {
  const ids = new Set(lista.map(i => i.id));
  return lista.map(i => {
    const precioLista = r2(i.precio_lista ?? i.precio);
    const pct    = Number(i.descuento_pct) || 0;
    const aplica = pct > 0 && i.paquete_de && ids.has(i.paquete_de);

    let cant = parseInt(i.cantidad) || 1;
    if (cant < 1) cant = 1;
    const max = (i.es_fisico && i.stock != null) ? parseInt(i.stock) : null;
    if (max != null && max > 0 && cant > max) cant = max;

    return {
      ...i,
      cantidad:         cant,
      precio_lista:     precioLista,
      precio:           aplica ? r2(precioLista * (1 - pct / 100)) : precioLista,
      descuento_activo: aplica,
    };
  });
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('comunicar_carrito');
      return saved ? normalizar(JSON.parse(saved)) : [];
    } catch {
      return [];
    }
  });
  const [ultimoAgregado, setUltimoAgregado] = useState(null);

  useEffect(() => {
    try { localStorage.setItem('comunicar_carrito', JSON.stringify(items)); } catch {}
  }, [items]);

  /* Agregar item — si ya está, no lo duplica */
  const agregarItem = useCallback((item) => {
    setItems(prev => {
      if (prev.find(i => i.id === item.id)) return prev;
      return normalizar([...prev, { ...item, cantidad: item.cantidad || 1 }]);
    });
    setUltimoAgregado(item);
  }, []);

  /* Cambiar cantidad (solo tiene sentido en productos físicos) */
  const cambiarCantidad = useCallback((id, cantidad) => {
    setItems(prev => normalizar(prev.map(i =>
      i.id === id ? { ...i, cantidad } : i
    )));
  }, []);

  const quitarItem = useCallback((id) => {
    setItems(prev => normalizar(prev.filter(i => i.id !== id)));
  }, []);

  const vaciarCarrito = useCallback(() => setItems([]), []);

  const limpiarUltimoAgregado = useCallback(() => setUltimoAgregado(null), []);

  const estaEnCarrito = useCallback((id) => items.some(i => i.id === id), [items]);

  /* Totales */
  const totalItems    = items.length;
  const totalUnidades = items.reduce((a, i) => a + (i.cantidad || 1), 0);
  const totalPrecio   = r2(items.reduce((a, i) => a + i.precio * (i.cantidad || 1), 0));
  const totalLista    = r2(items.reduce((a, i) => a + (i.precio_lista ?? i.precio) * (i.cantidad || 1), 0));
  const ahorroPaquete = r2(totalLista - totalPrecio);

  /* Tipo de carrito */
  const hayFisicos    = items.some(i => i.es_fisico);
  const hayDigitales  = items.some(i => !i.es_fisico);

  return (
    <CartContext.Provider value={{
      items,
      agregarItem,
      cambiarCantidad,
      quitarItem,
      vaciarCarrito,
      estaEnCarrito,
      limpiarUltimoAgregado,
      ultimoAgregado,
      totalItems,
      totalUnidades,
      totalPrecio,
      totalLista,
      ahorroPaquete,
      hayFisicos,
      hayDigitales,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart debe usarse dentro de CartProvider');
  return ctx;
}